import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Activity, Phone, ShieldCheck, Sparkles } from "lucide-react";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_55a9f4cb-4c33-47bd-86a7-80fe678bbbdb/artifacts/wavtipa0_PROKSY%20RESEARCH%20%282%29.png";

/* ---------- Animated SVG dashboard ---------- */
export function HeroDashboard() {
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1800);
        return () => clearInterval(id);
    }, []);

    // bars
    const bars = [38, 56, 42, 70, 60, 88, 74, 92, 65, 80, 58, 72];
    const offset = tick % bars.length;

    return (
        <div
            className="relative w-full max-w-[560px] mx-auto"
            data-testid="hero-dashboard"
        >
            {/* Floating glow */}
            <div className="absolute -inset-10 bg-blue-500/20 rounded-[40px] blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />

            <motion.div
                initial={{ opacity: 0, y: 30, rotate: -1 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
                className="pr-glass-strong rounded-3xl p-5 relative overflow-hidden"
            >
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
                        <span className="ml-3 text-[10px] font-mono-pr text-slate-400">
                            cati.proksy/live
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-mono-pr text-emerald-300">
                        <span className="relative w-2 h-2 rounded-full bg-emerald-400">
                            <span className="absolute inset-0 rounded-full bg-emerald-400/50 animate-ping" />
                        </span>
                        STREAMING
                    </div>
                </div>

                {/* KPI row */}
                <div className="grid grid-cols-3 gap-3">
                    <KPI
                        label="Response Rate"
                        value={`${(72 + (tick % 5)).toFixed(0)}%`}
                        accent="text-blue-300"
                    />
                    <KPI
                        label="Avg Length"
                        value={`${(8.4 + (tick % 4) * 0.1).toFixed(1)}m`}
                        accent="text-cyan-300"
                    />
                    <KPI
                        label="Quality Score"
                        value={`${(94 + (tick % 4)).toFixed(0)}`}
                        accent="text-emerald-300"
                    />
                </div>

                {/* SVG Line graph */}
                <div className="mt-4 h-32 relative">
                    <svg viewBox="0 0 320 120" className="w-full h-full">
                        <defs>
                            <linearGradient
                                id="lineGrad"
                                x1="0"
                                y1="0"
                                x2="1"
                                y2="0"
                            >
                                <stop offset="0%" stopColor="#3B82F6" />
                                <stop offset="100%" stopColor="#22C55E" />
                            </linearGradient>
                            <linearGradient
                                id="areaGrad"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#3B82F6"
                                    stopOpacity="0.45"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#3B82F6"
                                    stopOpacity="0"
                                />
                            </linearGradient>
                        </defs>
                        {/* grid */}
                        {[0, 30, 60, 90].map((y) => (
                            <line
                                key={y}
                                x1="0"
                                x2="320"
                                y1={y}
                                y2={y}
                                stroke="rgba(255,255,255,0.05)"
                            />
                        ))}
                        {/* Area + line */}
                        <motion.path
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{
                                duration: 2.4,
                                ease: "easeInOut",
                            }}
                            d="M0,90 C30,60 60,80 90,55 C120,30 150,55 180,40 C210,25 240,55 270,30 C290,12 305,28 320,18"
                            fill="none"
                            stroke="url(#lineGrad)"
                            strokeWidth="2.5"
                        />
                        <path
                            d="M0,90 C30,60 60,80 90,55 C120,30 150,55 180,40 C210,25 240,55 270,30 C290,12 305,28 320,18 L320,120 L0,120 Z"
                            fill="url(#areaGrad)"
                        />
                        {/* dots */}
                        {[
                            [60, 80],
                            [120, 30],
                            [180, 40],
                            [240, 55],
                            [300, 22],
                        ].map(([cx, cy], i) => (
                            <circle
                                key={i}
                                cx={cx}
                                cy={cy}
                                r="3"
                                fill="#22C55E"
                            />
                        ))}
                    </svg>
                </div>

                {/* Bars */}
                <div className="mt-4 flex items-end gap-1.5 h-20">
                    {bars.map((h, i) => {
                        const isHi = (i + offset) % bars.length === 5;
                        return (
                            <motion.div
                                key={i}
                                animate={{ height: `${h}%` }}
                                transition={{
                                    duration: 0.8,
                                    delay: i * 0.04,
                                    ease: "easeOut",
                                }}
                                className={`flex-1 rounded-md ${
                                    isHi
                                        ? "bg-gradient-to-t from-emerald-500/40 to-emerald-400 shadow-[0_0_18px_rgba(34,197,94,0.6)]"
                                        : "bg-gradient-to-t from-blue-600/30 to-blue-400/80"
                                }`}
                                style={{ height: `${h}%` }}
                            />
                        );
                    })}
                </div>

                {/* Live tags */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="pr-glass rounded-xl px-3 py-2.5 flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-400" />
                        <span className="font-mono-pr text-[11px] text-slate-300">
                            CATI · Live calls
                        </span>
                        <span className="ml-auto font-outfit text-sm text-white">
                            {142 + (tick % 9)}
                        </span>
                    </div>
                    <div className="pr-glass rounded-xl px-3 py-2.5 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span className="font-mono-pr text-[11px] text-slate-300">
                            Validated rows
                        </span>
                        <span className="ml-auto font-outfit text-sm text-white">
                            {(8.2 + (tick % 4) * 0.1).toFixed(1)}k
                        </span>
                    </div>
                </div>
            </motion.div>

            {/* Floating logo card */}
            <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.7 }}
                className="absolute -top-6 -left-6 animate-float"
            >
                <div className="pr-glass rounded-2xl p-3 flex items-center gap-3 border border-blue-400/30">
                    <div className="w-9 h-9 rounded-lg overflow-hidden border border-white/10">
                        <img
                            src={LOGO_URL}
                            alt="logo"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="leading-tight">
                        <p className="font-outfit text-[11px] text-blue-300 tracking-widest">
                            PROXY · ON
                        </p>
                        <p className="font-outfit text-xs text-white">
                            Listening to humans
                        </p>
                    </div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="absolute -bottom-6 -right-4 animate-float-slow"
            >
                <div className="pr-glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-emerald-400/30">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <div className="leading-tight">
                        <p className="font-outfit text-[10px] tracking-widest text-emerald-300">
                            INSIGHT GENERATED
                        </p>
                        <p className="font-outfit text-xs text-white">
                            +30% Brand recall in Q3
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

function KPI({ label, value, accent }) {
    return (
        <div className="pr-glass rounded-xl px-3 py-2.5">
            <p className="font-mono-pr text-[10px] tracking-wider text-slate-400 uppercase">
                {label}
            </p>
            <p className={`font-outfit text-lg font-bold ${accent}`}>{value}</p>
        </div>
    );
}

/* ---------- Background neural network of dots ---------- */
export function NetworkBackdrop() {
    const points = [
        [80, 60],
        [180, 120],
        [300, 80],
        [420, 160],
        [540, 90],
        [620, 200],
        [120, 240],
        [260, 320],
        [380, 280],
        [500, 360],
        [600, 320],
        [60, 380],
        [200, 440],
        [340, 420],
        [460, 480],
        [580, 460],
        [680, 380],
    ];
    const lines = [
        [0, 1],
        [1, 2],
        [2, 3],
        [3, 4],
        [4, 5],
        [0, 6],
        [6, 7],
        [7, 8],
        [8, 9],
        [9, 10],
        [6, 11],
        [11, 12],
        [12, 13],
        [13, 14],
        [14, 15],
        [15, 16],
        [10, 16],
        [3, 8],
        [4, 9],
    ];

    return (
        <svg
            className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
            viewBox="0 0 720 520"
            preserveAspectRatio="none"
        >
            <defs>
                <radialGradient id="dot" r="50%">
                    <stop offset="0%" stopColor="#60A5FA" stopOpacity="1" />
                    <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
                </radialGradient>
            </defs>
            {lines.map(([a, b], i) => {
                const [x1, y1] = points[a];
                const [x2, y2] = points[b];
                return (
                    <motion.line
                        key={i}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="rgba(59,130,246,0.18)"
                        strokeWidth="1"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{
                            duration: 2,
                            delay: i * 0.07,
                            ease: "easeInOut",
                        }}
                    />
                );
            })}
            {points.map(([x, y], i) => (
                <g key={i}>
                    <motion.circle
                        cx={x}
                        cy={y}
                        r="14"
                        fill="url(#dot)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0.7, 0.3] }}
                        transition={{
                            duration: 3,
                            delay: i * 0.15,
                            repeat: Infinity,
                            repeatType: "reverse",
                        }}
                    />
                    <motion.circle
                        cx={x}
                        cy={y}
                        r={i % 3 === 0 ? 2.5 : 1.6}
                        fill={i % 4 === 0 ? "#22C55E" : "#60A5FA"}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.06 }}
                    />
                </g>
            ))}
        </svg>
    );
}

/* ---------- HERO ---------- */
export default function Hero() {
    return (
        <section
            className="relative min-h-[100vh] flex items-center pt-32 pb-20 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Background layers */}
            <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none">
                <NetworkBackdrop />
            </div>
            <div className="absolute -top-32 -left-40 w-[480px] h-[480px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[460px] h-[460px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="pr-container relative grid lg:grid-cols-2 gap-12 items-center w-full">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <motion.span
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="pr-chip"
                        data-testid="hero-chip"
                    >
                        <Activity className="w-3.5 h-3.5" />
                        Your Proxy to Real Market Insights
                    </motion.span>

                    <h1
                        className="mt-6 font-outfit font-black text-5xl sm:text-6xl lg:text-[72px] leading-[1.05] tracking-tighter"
                        data-testid="hero-headline"
                    >
                        <span className="headline-gradient">
                            Real Human Insights.
                        </span>
                        <br />
                        <span className="text-white">
                            Powered by{" "}
                            <span className="accent-gradient">CATI.</span>
                        </span>
                    </h1>

                    <p
                        className="mt-6 max-w-xl text-slate-400 leading-relaxed text-base sm:text-lg"
                        data-testid="hero-subheadline"
                    >
                        We act as your proxy to capture real conversations and
                        turn them into actionable, decision-ready data — through
                        precision CATI fieldwork, validation, and analytics.
                    </p>

                    <div className="mt-9 flex flex-wrap gap-4">
                        <a
                            href="#lead-form"
                            className="btn-primary"
                            data-testid="hero-quote-btn"
                        >
                            Get a Quote
                        </a>
                        <a
                            href="#services"
                            className="btn-secondary"
                            data-testid="hero-services-btn"
                        >
                            Explore Services
                        </a>
                    </div>

                    <div className="mt-12 flex items-center gap-6 text-xs font-mono-pr text-slate-400">
                        <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>ISO-aligned QC</span>
                        </div>
                        <div className="hidden sm:block">·</div>
                        <div>Multilingual interviewers</div>
                        <div className="hidden sm:block">·</div>
                        <div>Real-time dashboard</div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 }}
                    className="relative"
                >
                    <HeroDashboard />
                </motion.div>
            </div>
        </section>
    );
}
